import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Search, Sparkles, Info } from "lucide-react";
import { apiClient } from "../../services/api";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";
import GuestBanner from "../../components/common/GuestBanner";
import RegistrationSheet from "../../components/common/RegistrationSheet";
import { useAuthStore } from "../../store/authStore";
import { isGuest } from "../../store/guestStore";

const CITIES = ["All", "Pune", "Bengaluru", "Mumbai"];
const PRO_TYPES = [
  { id: "all", label: "All" },
  { id: "trainer", label: "Trainers" },
  { id: "nutritionist", label: "Nutritionists" },
];

export default function TrainerBrowse() {
  const user = useAuthStore((s) => s.user);
  const guest = !user && isGuest();
  const [city, setCity] = useState("All");
  const [proType, setProType] = useState("all");
  const [specialisation, setSpecialisation] = useState(null);
  const [specCatalogue, setSpecCatalogue] = useState({ trainer: [], nutritionist: [] });
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [showReg, setShowReg] = useState(false);
  const [recs, setRecs] = useState(null);
  const [showWhy, setShowWhy] = useState(false);

  // Specialisation catalogue — one-shot load
  useEffect(() => {
    apiClient
      .get("/trainers/specialisations")
      .then((r) => setSpecCatalogue(r.data || { trainer: [], nutritionist: [] }))
      .catch(() => setSpecCatalogue({ trainer: [], nutritionist: [] }));
  }, []);

  // Reset specialisation when the pro-type changes so we never leak a
  // trainer chip into the nutritionist list.
  useEffect(() => {
    setSpecialisation(null);
  }, [proType]);

  const activeSpecList =
    proType === "nutritionist"
      ? specCatalogue.nutritionist || []
      : proType === "trainer"
      ? specCatalogue.trainer || []
      : [...(specCatalogue.trainer || []), ...(specCatalogue.nutritionist || [])];

  useEffect(() => {
    const params = {};
    if (city !== "All") params.city = city;
    if (proType !== "all") params.pro_type = proType;
    if (specialisation) params.specialisation = specialisation;
    if (q) params.search = q;
    apiClient
      .get("/trainers", { params })
      .then((r) => {
        // Backend returns { trainers: [...], total: N } — never assume it's a bare array.
        const list = Array.isArray(r.data) ? r.data : (r.data?.trainers || []);
        setItems(list);
      })
      .catch(() => setItems([]));
  }, [city, proType, specialisation, q]);

  // Smart-matching recs only for authed users
  useEffect(() => {
    if (!user) return;
    apiClient.get("/trainers/recommendations").then((r) => setRecs(r.data)).catch(() => setRecs(null));
  }, [user]);

  return (
    <div className="app-page" data-testid="trainer-browse">
      <TopBar title="Trainers" hideMenu={guest} back />
      <main className="max-w-2xl mx-auto px-4 pt-2 space-y-4" style={{ paddingBottom: guest ? 144 : 32 }}>
        <div className="flex items-center gap-2 bg-white rounded-pill h-12 px-4 border border-line">
          <Search size={18} className="text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a trainer or nutritionist…"
            data-testid="trainer-search"
            className="flex-1 bg-transparent outline-none text-[14px]"
          />
        </div>

        {/* Pro-type chips — Trainers / Nutritionists / All */}
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4" data-testid="pro-type-row">
          {PRO_TYPES.map((p) => {
            const active = proType === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setProType(p.id)}
                data-testid={`pro-type-${p.id}`}
                className="shrink-0 h-9 px-4 rounded-pill border"
                style={{
                  fontFamily: "Barlow, sans-serif",
                  fontWeight: 700,
                  fontSize: 13,
                  background: active ? "#0C0C0B" : "white",
                  color: active ? "#F5C518" : "#0C0C0B",
                  borderColor: active ? "#0C0C0B" : "var(--border)",
                  borderRadius: 100,
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        <div className="flex gap-2 overflow-x-auto -mx-4 px-4">
          {CITIES.map((c) => (
            <button
              key={c}
              onClick={() => setCity(c)}
              data-testid={`city-${c.toLowerCase()}`}
              className="px-4 h-9 shrink-0 rounded-pill text-[12px] border"
              style={{
                background: city === c ? "var(--accent-blue-dim)" : "white",
                borderColor: city === c ? "var(--accent-blue)" : "var(--border)",
                borderRadius: 100,
              }}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Specialisation chips — dynamically sourced from catalogue */}
        {activeSpecList.length > 0 && (
          <div
            className="flex gap-2 overflow-x-auto -mx-4 px-4"
            data-testid="specialisation-row"
            style={{ scrollbarWidth: "none" }}
          >
            <button
              onClick={() => setSpecialisation(null)}
              data-testid="spec-all"
              className="shrink-0 h-8 px-3 rounded-pill border text-[12px]"
              style={{
                background: !specialisation ? "#F5C518" : "white",
                color: "#0C0C0B",
                borderColor: !specialisation ? "#F5C518" : "var(--border)",
                borderRadius: 100,
                fontWeight: !specialisation ? 700 : 500,
              }}
            >
              Any specialisation
            </button>
            {activeSpecList.map((s) => {
              const active = specialisation === s;
              return (
                <button
                  key={s}
                  onClick={() => setSpecialisation(active ? null : s)}
                  data-testid={`spec-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="shrink-0 h-8 px-3 rounded-pill border text-[12px] whitespace-nowrap"
                  style={{
                    background: active ? "#F5C518" : "white",
                    color: "#0C0C0B",
                    borderColor: active ? "#F5C518" : "var(--border)",
                    borderRadius: 100,
                    fontWeight: active ? 700 : 500,
                  }}
                >
                  {s}
                </button>
              );
            })}
          </div>
        )}

        {/* Smart Recommendations strip */}
        {user && recs?.recommendations?.length > 0 && (
          <section data-testid="trainer-recommendations">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-barlow-cond font-extrabold text-[22px] flex items-center gap-1.5">
                <Sparkles size={16} style={{ color: "#00B0D5" }} /> Recommended for You
              </h2>
              <button onClick={() => setShowWhy(true)} data-testid="why-these" className="text-[12px] font-semibold text-ink-soft underline">Why these?</button>
            </div>
            <div className="flex gap-2.5 overflow-x-auto -mx-4 px-4" style={{ scrollbarWidth: "none" }}>
              {recs.recommendations.map((r) => (
                <Link
                  key={r.trainer_id}
                  to={`/trainers/${r.trainer_id}`}
                  data-testid={`rec-card-${r.trainer_id}`}
                  className="shrink-0 bg-white border border-line2 rounded-[14px] p-3 relative"
                  style={{ width: 200 }}
                >
                  <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-2 h-5 leading-5 rounded-pill" style={{ background: "rgba(0,212,255,0.18)", color: "#00B0D5", borderRadius: 100 }}>
                    {r.match_label}
                  </span>
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-canvas2 grid place-items-center">
                    {r.avatar_url ? <img src={r.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="text-[16px] font-semibold">{(r.name || "?")[0]}</span>}
                  </div>
                  <div className="font-bold text-[14px] mt-2 truncate">{r.name}</div>
                  <div className="text-[11px] text-ink-tertiary truncate">{r.city}</div>
                  <div className="text-[10.5px] text-ink-soft mt-1 line-clamp-2">{(r.specialisations || []).slice(0,2).join(" · ")}</div>
                </Link>
              ))}
            </div>
            <p className="text-[12px] text-ink-soft mt-2">
              <Link to="#all" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="trainer-list"]')?.scrollIntoView({ behavior: "smooth" }); }} data-testid="see-all-link" className="font-semibold underline">See all trainers ↓</Link>
            </p>
          </section>
        )}

        <div data-testid="trainer-list" />

        {items === null ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-28 bg-white rounded-[16px] border border-line2 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-ink-muted text-[14px] py-12">No trainers match this filter.</div>
        ) : (
          <div className="space-y-3">
            {items.map((t) => (
              <Link
                key={t.trainer_id}
                to={`/trainers/${t.trainer_id}`}
                data-testid={`trainer-${t.trainer_id}`}
                className="block bg-white rounded-[16px] p-4 border border-line2 transition-all hover:bg-canvas2"
                style={{ boxShadow: "var(--shadow-soft)" }}
              >
                <div className="flex items-center gap-3">
                  <img src={t.avatar_url} alt={t.name} className="w-16 h-16 rounded-full object-cover border border-line2" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-display text-[18px] leading-tight truncate">{t.name}</h3>
                      {t.pro_type === "nutritionist" && (
                        <span
                          data-testid={`nutri-badge-${t.trainer_id}`}
                          className="text-[9px] uppercase tracking-wider px-1.5 h-4 leading-4 rounded-pill"
                          style={{ background: "#DCF6F2", color: "#0F766E", fontWeight: 800, borderRadius: 100 }}
                        >
                          Nutritionist
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[12px] text-ink-soft mt-0.5">
                      <span className="capitalize">{t.city}</span>
                      {t.rating != null && (
                        <>
                          <span>·</span>
                          <span className="flex items-center gap-1">
                            <Star size={12} fill="currentColor" /> {t.rating} ({t.review_count || 0})
                          </span>
                        </>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {(t.specialisations || t.specialties || []).slice(0, 2).map((s) => (
                        <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-canvas2 text-ink-soft">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-[18px] leading-none">₹{(t.pricing_tiers?.[0]?.price_inr) ?? t.rate_inr ?? "—"}</div>
                    <div className="text-[11px] text-ink-muted">/ session</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      {guest ? <GuestBanner onSignUp={() => setShowReg(true)} /> : <BottomNav />}
      {showReg && (
        <RegistrationSheet
          context="general"
          onSuccess={() => setShowReg(false)}
          onDismiss={() => setShowReg(false)}
        />
      )}
      {showWhy && (
        <div className="fixed inset-0 z-[70] flex items-end bg-black/40" onClick={() => setShowWhy(false)} data-testid="why-sheet">
          <div className="w-full bg-white rounded-t-[20px] p-5" onClick={(e) => e.stopPropagation()} style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 20px)" }}>
            <div className="w-10 h-1 bg-line2 rounded-full mx-auto mb-3" />
            <h3 className="font-display text-[20px] flex items-center gap-1.5 mb-2"><Info size={16} /> Why these trainers?</h3>
            <p className="text-[14px] text-ink-soft" style={{ lineHeight: 1.6 }}>{recs?.explanation}</p>
            <button onClick={() => setShowWhy(false)} className="mt-4 w-full h-11 rounded-pill font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}
